import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';
import { shopTime, shopDateTime, shopDate, shopDateLong, clockDrift } from '../utils/time';
import {
  MagnifyingGlassIcon, CheckCircleIcon, ExclamationTriangleIcon,
  QrCodeIcon, UsersIcon, ReceiptPercentIcon, ArrowPathIcon,
  XCircleIcon, ClockIcon, ArrowDownTrayIcon,
  ListBulletIcon, ArrowPathRoundedSquareIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import styles from './ActivityPage.module.css';
import css from './CounterPage.module.css';
import MethodBadge from '../components/MethodBadge';

/**
 * The counter.
 *
 * Everything a cashier needs with a customer standing in front of them, on one
 * screen, because the alternative is telephoning the office while a queue
 * builds. Four things:
 *
 *   1. Look someone up and see where they stand — card, expiry, whether BC
 *      actually knows about them, who else may use the card, what they bought.
 *   2. Grant a membership that has been paid for on the POS.
 *   3. Check a QR code held up at the till, which works even when BC is down.
 *   4. See what the app took today, to settle "I already paid on my phone".
 *
 * Granting is the one action here, and it is a manual grant on the cashier's
 * word, so the invoice number is required and recorded against their name.
 */

const REL = { spouse:'Spouse', child:'Child', parent:'Parent', sibling:'Sibling', other:'Other' };

/** One number, said plainly. */
function Stat({ label, value, tone }) {
  return (
    <div className={`card ${css.stat}`}>
      <div className={css.statLabel}>{label}</div>
      <div className={css.statValue} style={tone ? { color: tone } : undefined}>
        {value ?? '—'}
      </div>
    </div>
  );
}

/**
 * What a scan means, in the cashier's terms.
 *
 * `matched` says the signature checks out; `wouldBeAccepted` says the till
 * would actually honour it. They differ for a blocked or expired card, and it
 * is the second one the person at the counter needs — a genuine card that the
 * POS will refuse is still a refusal, and saying "valid" would start an
 * argument the cashier cannot win.
 */
function QRResult({ r }) {
  const ok = r.matched && r.wouldBeAccepted;
  const m = r.member;
  const colour = ok ? '#047857' : '#B91C1C';

  return (
    <div style={{
      marginTop:14, padding:14, borderRadius:8,
      background: ok ? 'rgba(4,120,87,.07)' : 'rgba(185,28,28,.07)',
      border: `1px solid ${ok ? 'rgba(4,120,87,.25)' : 'rgba(185,28,28,.25)'}`,
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, fontWeight:700, color:colour }}>
        {ok ? <CheckCircleIcon style={{ width:20, height:20 }} />
            : <XCircleIcon style={{ width:20, height:20 }} />}
        {ok ? 'Accept this card' : r.matched ? 'Do not accept' : 'Not a valid code'}
      </div>

      {m && (
        <div style={{ marginTop:8 }}>
          <div style={{ fontSize:15, fontWeight:600 }}>{m.name || 'No name on record'}</div>
          <div style={{ fontSize:12, fontFamily:'monospace', color:'var(--text-muted)' }}>
            {m.cardNo}{m.scheme ? ` · ${m.scheme}` : ''}
          </div>
          <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>
            {m.phone && <>{m.phone} · </>}
            {m.expiryDate
              ? <>valid until {shopDateLong(m.expiryDate)}</>
              : 'no expiry on record'}
            {m.hasApp ? ' · has the app' : ' · no app account'}
          </div>
        </div>
      )}

      {/* Why, when the answer is no. The reasons are written for a person, so
          they are shown as they come. */}
      {(r.note || r.reason) && (
        <div style={{ marginTop:8, fontSize:13, color:'var(--text-muted)' }}>
          {r.note || r.reason}
        </div>
      )}

      {/* A family setup link scanned by mistake — common enough to point at
          the page holding the real code. */}
      {r.cardUrl && (
        <a href={r.cardUrl} target="_blank" rel="noreferrer"
           style={{ display:'inline-block', marginTop:8, fontSize:13 }}>
          Open their card page
        </a>
      )}
    </div>
  );
}

export default function CounterPage() {
  const [mobile, setMobile]     = useState('');
  const [looking, setLooking]   = useState(false);
  const [found, setFound]       = useState(null);
  const [error, setError]       = useState('');
  const [invoice, setInvoice]   = useState('');
  const [granting, setGranting] = useState(false);
  const [done, setDone]         = useState(null);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary]   = useState(null);
  // How far back the payments table reaches. One shop day by default; a
  // cashier asking "did this go through yesterday?" can widen it.
  const [days, setDays]         = useState(1);

  // Who is at the counter. A store login is the branch, not the person, so
  // the cashier picks their name once and it stays selected — retyping it for
  // every activation is how the field ends up blank.
  const [staff, setStaff]     = useState([]);
  const [staffId, setStaffId] = useState(() => localStorage.getItem('counter_staff') || '');

  // Export range. Quick day counts for the common case, explicit dates when
  // accounting asks for a particular month.
  const [exDays, setExDays] = useState(30);
  const [exFrom, setExFrom] = useState('');
  const [exTo,   setExTo]   = useState('');

  // Which panel is showing. Serving a customer is the default, since that is
  // what the page is for; the other two are checking and auditing.
  const [tab, setTab] = useState('serve');

  // Renewing rather than granting. Same evidence, different button, so the
  // invoice box is shared and this only decides which call is made.
  const [renewing, setRenewing] = useState(false);

  const [recent, setRecent]       = useState([]);
  const [recentDays, setRecentDays] = useState(1);
  const [refreshing, setRefreshing] = useState(false);

  // QR check — its own small panel, because it answers a different question
  // ("is this card real?") from the search box ("who is this?").
  const [qr, setQr]         = useState('');
  const [qrRes, setQrRes]   = useState(null);
  const [qrBusy, setQrBusy] = useState(false);

  const loadPayments = useCallback((d = days) => {
    adminAPI.storePayments({ days: d })
      .then(r => setPayments(r.payments || []))
      .catch(() => setPayments([]));
    adminAPI.storeSummary()
      .then(setSummary)
      .catch(() => setSummary(null));
  }, [days]);

  useEffect(() => { loadPayments(days); }, [loadPayments, days]);

  /**
   * Reload what is actually on screen.
   *
   * Takes no arguments on purpose: wiring a handler that accepts one straight
   * to onClick passes React's click event as that argument, which is how this
   * button previously asked the server for `days=[object Object]` — silently
   * served as one day, so refreshing the 7-day view quietly dropped it to
   * today without appearing to fail.
   */
  const handleRefresh = () => {
    setRefreshing(true);
    loadPayments(days);
    if (tab === 'recent') loadRecent(recentDays);
    // Long enough to be seen. A button that reports nothing looks broken even
    // when it worked, which is the complaint that found the bug above.
    setTimeout(() => setRefreshing(false), 600);
  };

  const loadRecent = useCallback((d = recentDays) => {
    adminAPI.storeRecent({ days: d })
      .then(r => setRecent(r.activations || []))
      .catch(() => setRecent([]));
  }, [recentDays]);

  useEffect(() => { if (tab === 'recent') loadRecent(recentDays); }, [tab, recentDays, loadRecent]);

  useEffect(() => {
    adminAPI.storeStaff()
      .then(r => setStaff(r.staff || []))
      .catch(() => setStaff([]));
  }, []);

  // Remembered across reloads: a POS terminal gets reloaded often and the
  // same person is usually still standing there.
  useEffect(() => {
    if (staffId) localStorage.setItem('counter_staff', staffId);
    else localStorage.removeItem('counter_staff');
  }, [staffId]);

  // A name the manager has retired must not stay silently selected.
  useEffect(() => {
    if (staffId && staff.length && !staff.some(m => m.id === staffId)) setStaffId('');
  }, [staff, staffId]);

  const handleRenew = async () => {
    if (!invoice.trim()) return setError('Enter the invoice number from the receipt.');
    if (staff.length && !staffId) return setError('Choose who is making this renewal.');

    const who = found?.user?.name || mobile;
    const me = staff.find(m => m.id === staffId);
    if (!window.confirm(
      `Renew ${who}'s VIP membership against invoice ${invoice.trim()}?

`
      + (me ? `This will be recorded against ${me.name}.

` : '')
      + 'Only do this if the renewal has been paid for at the counter.'
    )) return;

    setRenewing(true);
    setError('');
    try {
      const r = await adminAPI.storeRenew({
        mobile: mobile.trim(),
        invoiceNo: invoice.trim(),
        staffId: staffId || undefined,
      });
      setDone({ ...r, renewed: true });
      setFound(null);
      setInvoice('');
      loadPayments();
    } catch (err) {
      setError(err.message || 'Could not renew the membership.');
    } finally {
      setRenewing(false);
    }
  };

  // Exports. The manager reconciles manual activations against accounting, so
  // the file has to open in Excel with leading zeros and Arabic names intact —
  // that is handled server-side; here we only choose the range and name it.
  const handleExport = async (what) => {
    setError('');
    try {
      const params = (exFrom && exTo) ? { from: exFrom, to: exTo } : { days: exDays };
      const blob = await adminAPI.storeExport(what, params);
      const stamp = (exFrom && exTo) ? `${exFrom}_to_${exTo}` : `last${exDays}days`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${what === 'activations' ? 'manual-activations' : 'memberships'}-${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Could not build that export.');
    }
  };

  const reset = () => {
    setFound(null); setDone(null); setInvoice(''); setError(''); setCreateFor(false);
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!mobile.trim()) return;
    reset();
    setLooking(true);
    try {
      const r = await adminAPI.storeLookup(mobile.trim());
      setFound(r);
      if (!r.found) setError(r.message || 'No account for that number.');
    } catch (err) {
      setError(err.message || 'Could not look that number up.');
    } finally {
      setLooking(false);
    }
  };

  // Set when the cashier chooses to open an account for a customer who is in
  // Business Central but has never installed the app. Cleared on every new
  // lookup, so it can never carry over to the next person.
  const [createFor, setCreateFor] = useState(false);

  const handleActivate = async () => {
    if (!invoice.trim()) return setError('Enter the invoice number from the receipt.');
    if (staff.length && !staffId) return setError('Choose who is making this activation.');
    // Said out loud before it happens. This grants a paid membership without
    // a payment record, so the one guard that matters is the cashier reading
    // the sentence and thinking about it.
    // Name the person rather than the number, and say so when an account is
    // being created: the cashier is about to do two things and the prompt
    // only mentioned one of them.
    const who = found?.user?.name || found?.bc?.name || mobile;
    const me = staff.find(s => s.id === staffId);
    if (!window.confirm(
      (createFor
        ? `Open an app account for ${who} and give them VIP against invoice ${invoice.trim()}?\n\n`
          + 'A PIN will be shown once — read it out before leaving this screen.\n\n'
        : `Give ${who} a VIP membership against invoice ${invoice.trim()}?\n\n`)
      + (me ? `This will be recorded against ${me.name}.\n\n` : '')
      + 'Only do this if the membership has been paid for at the counter.'
    )) return;

    setGranting(true);
    setError('');
    try {
      const r = await adminAPI.storeActivate({
        mobile: mobile.trim(),
        invoiceNo: invoice.trim(),
        staffId: staffId || undefined,
        // Set only when the cashier has agreed to open an account for
        // somebody who has not installed the app.
        createIfMissing: createFor || undefined,
        firstName: createFor ? (found?.bc?.name || '').split(' ')[0] : undefined,
      });
      setDone(r);
      setFound(null);
      loadPayments();
    } catch (err) {
      setError(err.message || 'Could not activate the membership.');
    } finally {
      setGranting(false);
    }
  };

  /**
   * Undo a grant made here by mistake.
   *
   * The reason is required and typed, not picked from a list: "wrong number"
   * and "customer changed their mind" are different conversations later, and
   * a dropdown would collapse them into whichever option is first.
   */
  const handleReverse = async (row) => {
    const reason = window.prompt(
      `Reverse the VIP given to ${row.name || row.mobile} on invoice ${row.invoiceNo}?

`
      + 'Why is it being reversed?'
    );
    if (reason === null) return;
    if (reason.trim().length < 4) {
      setError('Say briefly why this is being reversed.');
      return;
    }

    setError('');
    try {
      const r = await adminAPI.storeReverse({
        activationId: row.id,
        reason: reason.trim(),
        staffId: staffId || undefined,
      });
      // Said out loud, because the money is a separate action and this is
      // exactly where a refund gets forgotten.
      window.alert(r.note || 'Membership withdrawn.');
      loadRecent(recentDays);
      loadPayments(days);
    } catch (err) {
      setError(err.message || 'Could not reverse that activation.');
    }
  };

  const handleQR = async (e) => {
    e.preventDefault();
    if (!qr.trim()) return;
    setQrBusy(true); setQrRes(null);
    try {
      setQrRes(await adminAPI.storeQR(qr.trim(), staffId || undefined));
    } catch (err) {
      setQrRes({ matched: false, reason: err.message || 'Could not check that code.' });
    } finally {
      setQrBusy(false);
    }
  };

  const alreadyMember = found?.membership?.isActive;

  // Computed on render rather than stored: the comparison is only meaningful
  // against the moment the summary was fetched.
  const drift = summary?.serverTime ? clockDrift(summary.serverTime) : null;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Counter</h1>
          <p>Look up a member, check a card, or activate a membership paid for at the counter</p>
        </div>
        <div className="page-header-actions">
          <button
            className="btn-ghost"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Reload what is on screen"
          >
            <ArrowPathIcon
              style={{ width:15, height:15 }}
              className={refreshing ? css.spin : undefined}
            />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* This terminal's clock, when it is wrong. Times on screen are rendered
          from the server's instant in Beirut time, so they stay right — but a
          cashier comparing the screen to a drifting till display needs to know
          which one to doubt. */}
      {drift !== null && Math.abs(drift) >= 120 && (
        <div className="card" style={{
          padding:'10px 14px', marginBottom:12,
          display:'flex', alignItems:'center', gap:8,
          background:'rgba(180,83,9,.07)', border:'1px solid rgba(180,83,9,.25)',
        }}>
          <ClockIcon style={{ width:16, height:16, color:'#B45309', flexShrink:0 }} />
          <span style={{ fontSize:13, color:'#B45309' }}>
            This terminal's clock is {Math.abs(Math.round(drift / 60))} minutes
            {drift > 0 ? ' ahead of' : ' behind'} the server. Times shown here are correct;
            the terminal's own clock needs setting.
          </span>
        </div>
      )}

      {/* Three things happen at a counter: serving someone, checking a card,
          and looking back at what was done. One tab each. */}
      <div className={css.tabs}>
        {[
          { id:'serve',  label:'Serve a customer', Icon:MagnifyingGlassIcon },
          { id:'qr',     label:'Check a card',     Icon:QrCodeIcon },
          { id:'recent', label:'Done here',        Icon:ListBulletIcon },
        ].map(({ id, label, Icon }) => (
          <button
            key={id}
            className={`${css.tab} ${tab === id ? css.tabActive : ''}`}
            onClick={() => setTab(id)}
          >
            <Icon style={{ width:16, height:16 }} />
            {label}
            {id === 'recent' && summary?.activatedAtTill > 0 && (
              <span className={css.tabCount}>{summary.activatedAtTill}</span>
            )}
          </button>
        ))}
      </div>

      {/* Today, in four numbers. On the serving tab these are glanceable
          context, not the job — the search below is the job — so they shrink
          to a strip and the search gets the weight. */}
      <div className={`${css.stats} ${tab === 'serve' ? css.statsSlim : ''}`}>
        <Stat label="Paid in app"     value={summary?.paidInApp} />
        <Stat label="Pending in app"  value={summary?.pendingInApp}
              tone={summary?.pendingInApp ? '#B45309' : undefined} />
        <Stat label="Activated here"  value={summary?.activatedAtTill} />
        <Stat label="By you"          value={summary?.activatedByYou} />
      </div>

      {tab === 'serve' && <>
      {/* Find them. The whole point of the screen, so it is the biggest
          thing on it and holds focus on load — a cashier should be able to
          start typing a number without reaching for the mouse. */}
      <div className={`card ${css.findCard}`}>
        <form onSubmit={handleLookup} className={css.findRow}>
          <MagnifyingGlassIcon className={css.findIcon} />
          <input
            className={css.bigInput}
            placeholder="Mobile number"
            value={mobile}
            onChange={e => setMobile(e.target.value)}
            inputMode="tel"
            autoComplete="off"
            autoFocus
          />
          {mobile && (
            <button type="button" className={css.clearInput}
                    onClick={() => { setMobile(''); reset(); }} title="Clear">
              <XMarkIcon style={{ width:18, height:18 }} />
            </button>
          )}
          <button className={`btn-primary ${css.bigBtn}`} type="submit" disabled={looking}>
            {looking ? 'Looking…' : 'Find'}
          </button>
        </form>
      </div>

      {error && (
        <div className="card" style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 10 }}>
          <ExclamationTriangleIcon style={{ width: 18, height: 18, color: 'var(--warning, #b45309)', flexShrink: 0 }} />
          <div>{error}</div>
        </div>
      )}

      {/* Just granted */}
      {done && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
            <CheckCircleIcon style={{ width: 22, height: 22, color: 'var(--success, #047857)' }} />
            <h2 style={{ margin: 0 }}>
              {done.name || 'Member'} {done.renewed ? 'is renewed' : 'is now VIP'}
            </h2>
          </div>
          <p style={{ margin: '4px 0', color: 'var(--text-muted)' }}>
            Card <strong>{done.memberCode}</strong>
            {done.expiryDate && <> · valid until {shopDateLong(done.expiryDate)}</>}
          </p>
          {done.pin ? (
            // Shown once. The PIN is hashed the moment it is made, so there
            // is no second chance to read it — which is why it is the loudest
            // thing in this panel.
            <div style={{
              margin: '12px 0', padding: '14px 16px', borderRadius: 10,
              background: 'rgba(245,200,66,.12)', border: '1px solid rgba(245,200,66,.4)',
            }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                Read this out — it is not shown again
              </div>
              <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: 6, fontFamily: 'monospace' }}>
                {done.pin}
              </div>
              <div style={{ fontSize: 13, marginTop: 6 }}>
                They sign in to the app with their mobile number and this PIN.
              </div>
            </div>
          ) : (
            <p style={{ margin: '4px 0', color: 'var(--text-muted)' }}>
              Ask them to open the app and pull down to refresh.
            </p>
          )}
          {done.renewed && done.renewalCount > 0 && (
            <p style={{ margin: '4px 0', fontSize: 12, color: 'var(--text-muted)' }}>
              Renewal number {done.renewalCount}.
            </p>
          )}
          {done.staffName && (
            <p style={{ margin: '4px 0', fontSize: 12, color: 'var(--text-muted)' }}>
              Recorded against {done.staffName}.
            </p>
          )}
        </div>
      )}

      {/* Found them */}
      {found?.found && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <div className={css.memberHead}>
            <div>
              <h2 style={{ marginTop: 0, marginBottom: 2 }}>{found.user.name || 'No name on the account'}</h2>
              <p style={{ color: 'var(--text-muted)', margin: 0 }}>
                {found.user.mobile}
                {found.user.email && <> · {found.user.email}</>}
              </p>
            </div>
            {found.membership && <MethodBadge source={found.membership.source} size={13} />}
          </div>

          {alreadyMember ? (
            <div style={{ marginTop: 14 }}>
              <span className="badge badge-active">Active VIP member</span>
              <p style={{ color: 'var(--text-muted)', marginBottom: 6 }}>
                Card <strong>{found.membership.cardNo || found.membership.memberCode}</strong>
                {found.membership.expiryDate && <> · valid until {shopDateLong(found.membership.expiryDate)}</>}
                {found.membership.daysLeft != null && <> · {found.membership.daysLeft} days left</>}
              </p>

              {/* The two things that actually go wrong at a till. */}
              {found.membership.expiringSoon && (
                <p style={{ color:'#B45309', fontSize:13, margin:'6px 0', display:'flex', alignItems:'center', gap:6 }}>
                  <ClockIcon style={{ width:15, height:15 }} />
                  Expires in {found.membership.daysLeft} days — offer them a renewal.
                </p>
              )}

              {/* Renewing an active card. Offered whenever they hold one, not
                  only when it is nearly up: somebody who wants to pay today
                  should be able to, and the extension runs from their current
                  expiry so they lose nothing by being early. */}
              <div style={{ marginTop:14, paddingTop:14, borderTop:'1px solid var(--border)' }}>
                <p style={{ marginTop:0, marginBottom:10, fontSize:13 }}>
                  Renewing? Enter the invoice number for the renewal.
                  {found.membership.daysLeft > 0 && (
                    <span style={{ color:'var(--text-muted)' }}>
                      {' '}Their remaining {found.membership.daysLeft} days are added on.
                    </span>
                  )}
                </p>
                <div className={css.grantRow}>
                  <input
                    className={`${styles.searchInput} ${css.invoiceInput}`}
                    placeholder="Invoice number"
                    value={invoice}
                    onChange={e => setInvoice(e.target.value)}
                    inputMode="numeric"
                  />
                  {staff.length > 0 && (
                    <select
                      className={`${styles.searchInput} ${css.staffSelect}`}
                      value={staffId}
                      onChange={e => setStaffId(e.target.value)}
                      aria-label="Who is making this renewal"
                    >
                      <option value="">Who are you?</option>
                      {staff.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  )}
                  <button className={`btn-primary ${css.findBtn}`} onClick={handleRenew} disabled={renewing}>
                    <ArrowPathRoundedSquareIcon style={{ width:15, height:15 }} />
                    {renewing ? 'Renewing…' : 'Renew for a year'}
                  </button>
                </div>
              </div>
              {!found.membership.bcSynced && (
                <p style={{ color:'#B45309', fontSize:13, margin:'6px 0', display:'flex', alignItems:'center', gap:6 }}>
                  <ExclamationTriangleIcon style={{ width:15, height:15 }} />
                  Not yet in Business Central — the POS may not recognise this card. It syncs automatically.
                </p>
              )}
            </div>
          ) : (
            <div style={{ marginTop: 14 }}>
              <p style={{ marginBottom: 12 }}>
                {found.membership
                  ? <>Membership is <strong>{found.membership.status}</strong>. Enter the invoice number to renew it for another year.</>
                  : 'Not a member yet. Enter the invoice number from the receipt to activate.'}
              </p>
              <div className={css.grantRow}>
                <input
                  className={`${styles.searchInput} ${css.invoiceInput}`}
                  placeholder="Invoice number"
                  value={invoice}
                  onChange={e => setInvoice(e.target.value)}
                  inputMode="numeric"
                />
                {/* Who is at the counter. Hidden when the manager has not set
                    up this branch's list, so an unconfigured branch can still
                    serve a customer. */}
                {staff.length > 0 && (
                  <select
                    className={`${styles.searchInput} ${css.staffSelect}`}
                    value={staffId}
                    onChange={e => setStaffId(e.target.value)}
                    aria-label="Who is making this activation"
                  >
                    <option value="">Who are you?</option>
                    {staff.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                )}
                {/* An expired member already has a membership row, so
                    granting a second one would be refused. Renew theirs. */}
                {found.membership ? (
                  <button className={`btn-primary ${css.findBtn}`} onClick={handleRenew} disabled={renewing}>
                    <ArrowPathRoundedSquareIcon style={{ width:15, height:15 }} />
                    {renewing ? 'Renewing…' : 'Renew VIP'}
                  </button>
                ) : (
                  <button className={`btn-primary ${css.findBtn}`} onClick={handleActivate} disabled={granting}>
                    {granting ? 'Activating…' : 'Activate VIP'}
                  </button>
                )}
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
                {staff.length > 0
                  ? 'Recorded against the name you choose. Only use this for a membership that has been paid for.'
                  : 'Recorded against this counter. Only use this for a membership that has been paid for.'}
              </p>
            </div>
          )}

          {/* Their last payment attempt — "I already paid on my phone" is
              usually true, and usually still pending. */}
          {found.lastPayment && (
            <div style={{ marginTop:16, paddingTop:14, borderTop:'1px solid var(--border)' }}>
              <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:6 }}>Last payment attempt</div>
              <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap', fontSize:13 }}>
                <span>{found.lastPayment.at ? shopDateTime(found.lastPayment.at) : '—'}</span>
                <span>·</span>
                <strong>{found.lastPayment.currency === 'USD' ? '$' : ''}{found.lastPayment.amount.toFixed(2)}</strong>
                <span>·</span>
                <span>{found.lastPayment.method}</span>
                {found.lastPayment.status === 'success'
                  ? <span className="badge badge-active">Paid</span>
                  : <span className="badge badge-inactive">{found.lastPayment.status}</span>}
              </div>
            </div>
          )}

          {/* Who else may use the card. */}
          {found.family?.length > 0 && (
            <div style={{ marginTop:16, paddingTop:14, borderTop:'1px solid var(--border)' }}>
              <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
                <UsersIcon style={{ width:14, height:14 }} /> Family on this card
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {found.family.map((f, i) => (
                  <span key={i} style={{
                    fontSize:12, padding:'4px 10px', borderRadius:999,
                    border:'1px solid var(--border)',
                    opacity: f.isActive ? 1 : 0.45,
                    textDecoration: f.isActive ? 'none' : 'line-through',
                  }}>
                    {f.name} <span style={{ color:'var(--text-muted)' }}>· {REL[f.relationship] || f.relationship}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Recent receipts, for "I bought this here last week". */}
          {found.recentPurchases?.length > 0 && (
            <div style={{ marginTop:16, paddingTop:14, borderTop:'1px solid var(--border)' }}>
              <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
                <ReceiptPercentIcon style={{ width:14, height:14 }} /> Recent purchases
              </div>
              <table className="data-table">
                <tbody>
                  {found.recentPurchases.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontSize:12, whiteSpace:'nowrap' }}>
                        {r.at ? shopDate(r.at) : '—'}
                      </td>
                      <td style={{ fontSize:12, fontFamily:'monospace', color:'var(--text-muted)' }}>{r.receiptNo}</td>
                      <td style={{ fontSize:12 }}>{r.itemCount} item{r.itemCount === 1 ? '' : 's'}</td>
                      <td style={{ fontSize:12, fontWeight:600, whiteSpace:'nowrap' }}>
                        {r.isRefund && <span style={{ color:'#B45309' }}>refund </span>}
                        {r.total != null ? r.total.toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Known to the shop, no app account. They have paid and are standing
          there, so the counter opens the account rather than sending them
          away to install something first. */}
      {found && !found.found && found.inBusinessCentral && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <h2 style={{ marginTop: 0 }}>{found.bc?.name || 'Known customer'}</h2>
          <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>
            In Business Central, but has not set up the app yet.
          </p>

          <div className={css.grantRow}>
            <input
              className={`${styles.searchInput} ${css.invoiceInput}`}
              placeholder="Invoice number"
              value={invoice}
              onChange={e => setInvoice(e.target.value)}
              inputMode="numeric"
            />
            {staff.length > 0 && (
              <select
                className={`${styles.searchInput} ${css.staffSelect}`}
                value={staffId}
                onChange={e => setStaffId(e.target.value)}
                aria-label="Who is making this activation"
              >
                <option value="">Who are you?</option>
                {staff.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            )}
            <button
              className={`btn-primary ${css.findBtn}`}
              onClick={() => { setCreateFor(true); setTimeout(handleActivate, 0); }}
              disabled={granting}
            >
              {granting ? 'Activating…' : 'Open account & activate'}
            </button>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
            An account is created for them using the name on their Business Central
            record, and a PIN is shown here once for you to read out. They sign in
            with their mobile number and that PIN.
          </p>
        </div>
      )}

      </>}

      {tab === 'qr' && (
      /* Check a QR */
      <div className="card" style={{ padding:20, marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
          <QrCodeIcon style={{ width:18, height:18, color:'var(--text-muted)' }} />
          <strong>Check a VIP QR code</strong>
          <span style={{ color:'var(--text-muted)', fontSize:12 }}>
            scan into the box, or paste the code
          </span>
        </div>
        <form onSubmit={handleQR} className={css.findRow}>
          <input
            className={`${styles.searchInput} ${css.findInput}`}
            style={{ fontFamily:'monospace' }}
            placeholder="Scan or paste the QR value"
            value={qr}
            onChange={e => setQr(e.target.value)}
          />
          <button className={`btn-primary ${css.findBtn}`} type="submit" disabled={qrBusy}>
            {qrBusy ? 'Checking…' : 'Check'}
          </button>
          {qrRes && (
            <button type="button" className={`btn-ghost ${css.findBtn}`}
                    onClick={() => { setQr(''); setQrRes(null); }}>
              Clear
            </button>
          )}
        </form>

        {qrRes && <QRResult r={qrRes} />}
      </div>
      )}

      {tab === 'recent' && <>
      {/* What this counter has done. A mistyped invoice is far cheaper to
          notice now than when the manager finds it in a monthly export. */}
      <div className="card" style={{ padding:0, marginBottom:16, overflow:'hidden' }}>
        <div className={css.tableHead}>
          <strong>Activated at this counter</strong>
          <span style={{ color:'var(--text-muted)' }}>this branch only</span>
          <div className={css.rangeBtns}>
            {[{ d:1, t:'Today' }, { d:7, t:'7 days' }, { d:14, t:'14 days' }].map(({ d, t }) => (
              <button
                key={d}
                className={`${d === recentDays ? 'btn-primary' : 'btn-ghost'} ${css.rangeBtn}`}
                onClick={() => setRecentDays(d)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <div className="table-wrap" style={{ border:'none', borderRadius:0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th><th>What</th><th>Member</th><th>Phone</th>
                <th>Invoice</th><th>By</th><th>Expires</th><th>In BC</th><th></th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 ? (
                <tr><td colSpan={9}>
                  <div className="empty-state">
                    <p>{recentDays === 1
                      ? 'Nothing activated at this counter yet today.'
                      : `Nothing activated here in the last ${recentDays} days.`}</p>
                  </div>
                </td></tr>
              ) : recent.map((r, i) => (
                <tr key={i} style={{ opacity: r.reversedAt ? 0.5 : 1 }}>
                  <td style={{ whiteSpace:'nowrap' }}>
                    {r.at ? recentDays === 1 ? shopTime(r.at) : shopDateTime(r.at) : '—'}
                  </td>
                  <td>
                    {r.reversedAt
                      ? <span className="badge badge-inactive">Reversed</span>
                      : r.action === 'renew'
                        ? <span className="badge badge-warning">Renewal</span>
                        : <span className="badge badge-active">New card</span>}
                  </td>
                  <td>{r.name || '—'}</td>
                  <td style={{ whiteSpace:'nowrap' }}>{r.mobile}</td>
                  <td style={{ fontFamily:'monospace', fontSize:12 }}>{r.invoiceNo}</td>
                  <td style={{ fontSize:12 }}>{r.staffName || <span style={{ color:'var(--text-subtle)' }}>—</span>}</td>
                  <td style={{ fontSize:12, whiteSpace:'nowrap' }}>
                    {r.expiryDate ? shopDate(r.expiryDate) : '—'}
                  </td>
                  <td>
                    {r.bcSynced
                      ? <CheckCircleIcon style={{ width:16, height:16, color:'#047857' }} />
                      : <span style={{ fontSize:11, color:'#B45309' }}>pending</span>}
                  </td>
                  <td style={{ whiteSpace:'nowrap' }}>
                    {/* Only today's, and only a new card. A renewal needs the
                        previous expiry restored, which the server refuses to
                        guess at. */}
                    {!r.reversedAt && recentDays === 1 && r.action !== 'renew' && (
                      <button
                        className="btn-ghost"
                        style={{ padding:'4px 10px', fontSize:12 }}
                        onClick={() => handleReverse(r)}
                      >
                        Reverse
                      </button>
                    )}
                    {r.reversedAt && (
                      <span style={{ fontSize:11, color:'var(--text-subtle)' }}
                            title={r.reversalReason || undefined}>
                        undone
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Exports, for the manager rather than the cashier. Placed last
          because it is a monthly job, not a per-customer one. */}
      <div className="card" style={{ padding:20, marginBottom:16 }}>
        <div className={css.exportHead}>
          <ArrowDownTrayIcon style={{ width:18, height:18, color:'var(--text-muted)' }} />
          <strong>Export for audit</strong>
          <span className={css.exportHint}>CSV, opens in Excel</span>
        </div>

        {/* The range applies to both files, so it is chosen once above them
            rather than repeated on each. */}
        <div className={css.exportRange}>
          <span className={css.exportLabel}>Period</span>
          <div className={css.segmented}>
            {[7, 30, 90].map(d => (
              <button
                key={d}
                className={`${css.segment} ${(!exFrom && !exTo && d === exDays) ? css.segmentOn : ''}`}
                onClick={() => { setExDays(d); setExFrom(''); setExTo(''); }}
              >
                {d} days
              </button>
            ))}
          </div>
          <span className={css.exportSep}>or</span>
          <div className={css.dateGroup}>
            <input type="date" className={css.exportDate} value={exFrom}
                   onChange={e => setExFrom(e.target.value)} aria-label="From date" />
            <span className={css.exportSep}>to</span>
            <input type="date" className={css.exportDate} value={exTo}
                   onChange={e => setExTo(e.target.value)} aria-label="To date" />
            {(exFrom || exTo) && (
              <button className={css.clearDates}
                      onClick={() => { setExFrom(''); setExTo(''); }}
                      title="Back to a fixed period">
                <XMarkIcon style={{ width:14, height:14 }} />
              </button>
            )}
          </div>
        </div>

        {/* Each file says what it is, so the explanation sits with the thing
            it explains rather than in a paragraph underneath both. */}
        <div className={css.exportCards}>
          <button className={css.exportCard} onClick={() => handleExport('activations')}>
            <span className={css.exportCardHead}>
              <ArrowDownTrayIcon style={{ width:15, height:15 }} />
              Manual activations
            </span>
            <span className={css.exportCardNote}>
              Granted at the counter against an invoice — the memberships with no
              card or Whish receipt behind them.
            </span>
          </button>

          <button className={css.exportCard} onClick={() => handleExport('memberships')}>
            <span className={css.exportCardHead}>
              <ArrowDownTrayIcon style={{ width:15, height:15 }} />
              All memberships sold
            </span>
            <span className={css.exportCardNote}>
              Every sale in the period however it was paid for, with the gateway
              reference or invoice number against each.
            </span>
          </button>
        </div>
      </div>

      {/* Paid in the app today */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className={css.tableHead}>
          <strong>Paid in the app</strong>
          <span style={{ color:'var(--text-muted)' }}>
            card and Whish, newest first
          </span>
          <div className={css.rangeBtns}>
            {[{ d:1, t:'Today' }, { d:3, t:'3 days' }, { d:7, t:'7 days' }].map(({ d, t }) => (
              <button
                key={d}
                className={`${d === days ? 'btn-primary' : 'btn-ghost'} ${css.rangeBtn}`}
                onClick={() => setDays(d)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th><th>Who</th><th>Phone</th>
                <th>Method</th><th>Amount</th><th>Result</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan={6}>
                  <div className="empty-state">
                    <p>
                      {days === 1
                        ? 'Nobody has paid in the app yet today.'
                        : `No app payments in the last ${days} days.`}
                    </p>
                  </div>
                </td></tr>
              ) : payments.map(p => (
                <tr key={p.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {p.at ? days === 1 ? shopTime(p.at) : shopDateTime(p.at) : '—'}
                  </td>
                  <td>{p.name || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{p.mobile}</td>
                  <td>{p.method}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {p.currency === 'USD' ? '$' : ''}{p.amount.toFixed(2)}
                  </td>
                  <td>
                    {p.status === 'success'
                      ? <span className="badge badge-active">Paid</span>
                      : <span className="badge badge-inactive">{p.status}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>}
    </div>
  );
}
